import type { CodecResult } from "@schema-benchmarks/bench";
import { useMemo } from "react";

import type { SortableKey } from "#src/routes/_benchmarks/_runtime/codec/-constants";
import { EmptyState } from "#src/shared/components/empty-state";
import { MdSymbol } from "#src/shared/components/symbol";
import { Bar } from "#src/shared/components/table/bar";
import { useBreakpoints } from "#src/shared/hooks/use-breakpoints";
import type { SortDirection } from "#src/shared/lib/sort";

import { CodecCard } from "./card";
import { CodecTable } from "./table";

export interface CodecResultsProps {
  results: Array<CodecResult>;
  sortBy: SortableKey;
  sortDir: SortDirection;
}

export function CodecResults({ results, sortBy, sortDir }: CodecResultsProps) {
  const shouldUseTable = useBreakpoints(["laptop", "desktop"], true);
  const encodeScaler = useMemo(
    () =>
      Bar.getScale(
        results.map((result) => result.encode.mean),
        { lowerBetter: true },
      ),
    [results],
  );
  const decodeScaler = useMemo(
    () =>
      Bar.getScale(
        results.map((result) => result.decode.mean),
        { lowerBetter: true },
      ),
    [results],
  );
  if (!results.length) {
    return (
      <EmptyState
        icon={<MdSymbol>database_off</MdSymbol>}
        title="No results found"
        subtitle="Try a different combination of filters"
      />
    );
  }
  return (
    <div suppressHydrationWarning>
      {shouldUseTable ? (
        <CodecTable {...{ results, encodeScaler, decodeScaler, sortBy, sortDir }} />
      ) : (
        <ul className="bench-cards" aria-label="Results">
          {results.map((result) => (
            <CodecCard key={result.id} {...{ result, encodeScaler, decodeScaler }} />
          ))}
        </ul>
      )}
    </div>
  );
}
